import React, { useState, useEffect } from "react";
import {
  Table,
  Form,
  InputGroup,
  Tab,
  Tabs,
  Container,
  Row,
  Col,
  Button,
  Card,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import ImageNoDataFound from "../../../images/nodata_found.gif";
import { RxCaretSort } from "react-icons/rx";
import { IoWarningOutline } from "react-icons/io5";
import { CiSearch } from "react-icons/ci";
import axios from "axios";
import apiConfig from "../../credentials/apiConfig";
import { toast } from "react-toastify";
import "./styles.scss";
import { useNavigate } from "react-router-dom";
import image from "../../../images/ai-robot.gif";
import Loader from "../../../components/Loader";
import ReactTextTransition from "react-text-transition";


const initialMessages = [
  "AI is analyzing your data. Please wait...",
  "Hang tight! Your analysis is in progress..."
];

const repeatMessages = [
  "Fetching your results. Almost there...",
  "Great things take time. Preparing your results now...",
  "Just a little longer. Thanks for your patience!"
];


const Mapping = ({
  onStepComplete,
  mappingData,
  handleNext,
  handlePrevious,
  setParData,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("currentYear");
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentYearData, setCurrentYearData] = useState([]);
  const [priorYearData, setPriorYearData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [
    currentYearLowConfidenceCount,
    setCurrentYearLowConfidenceCount,
  ] = useState(0);
  const [
    priorYearLowConfidenceCount,
    setPriorYearLowConfidenceCount,
  ] = useState(0);
  const [engagementId, setEngagementId] = useState("");

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [subgroupOptions, setSubgroupOptions] = useState([]);
  const [isGeneratingPar, setIsGeneratingPar] = useState(false);
  const [saveExitLoading, setSaveExitLoading] = useState(false);
  const [continueParLoading, setContinueParLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [extraMessage, setExtraMessage] = useState("");

  useEffect(() => {
    let messageTimer, elapsedTimer;

    if (isPolling) {
      setCurrentMessageIndex(0);
      setElapsedSeconds(0);
      setExtraMessage("");

      messageTimer = setInterval(() => {
        setCurrentMessageIndex(prev => prev + 1);
      }, 5000);

      elapsedTimer = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    }

    return () => {
      clearInterval(messageTimer);
      clearInterval(elapsedTimer);
    };
  }, [isPolling]);

  useEffect(() => {
    if (elapsedSeconds >= 300) {
      setExtraMessage("Thanks for your patience! Almost done.");
    } else if (elapsedSeconds >= 180) {
      setExtraMessage("Almost there. Please don’t close this tab.");
    } else if (elapsedSeconds >= 60) {
      setExtraMessage("Still working... Some files may take longer.");
    }
  }, [elapsedSeconds]);




  const getMessage = () => {
    if (currentMessageIndex < initialMessages.length) {
      return initialMessages[currentMessageIndex];
    } else {
      const loopIndex = (currentMessageIndex - initialMessages.length) % repeatMessages.length;
      return repeatMessages[loopIndex];
    }
  };


  useEffect(() => {
    if (!engagementId) return;

    let pollingInterval = null;

    const processMappingData = (data) => {
      // Create normalized lookup maps first
      const categoryMap = new Map();
      const groupMap = new Map();
      const subgroupMap = new Map();

      // Build lookup maps from all data
      [...(data.currentYearTrialBalance?.aiClassifiedData || []), ...(data.priorYearTrialBalance?.aiClassifiedData || [])].forEach(item => {
        if (item.Category && item.Category.trim()) {
          const trimmed = item.Category.trim();
          const normalized = trimmed.toLowerCase();
          if (!categoryMap.has(normalized)) {
            categoryMap.set(normalized, trimmed);
          }
        }
        if (item.Group && item.Group.trim()) {
          const trimmed = item.Group.trim();
          const normalized = trimmed.toLowerCase();
          if (!groupMap.has(normalized)) {
            groupMap.set(normalized, trimmed);
          }
        }
        if (item.Subgroup && item.Subgroup.trim()) {
          const trimmed = item.Subgroup.trim();
          const normalized = trimmed.toLowerCase();
          if (!subgroupMap.has(normalized)) {
            subgroupMap.set(normalized, trimmed);
          }
        }
      });

      // Helper function to normalize values
      const normalizeValue = (value, map) => {
        if (!value || !value.trim()) return "";
        const normalized = value.trim().toLowerCase();
        return map.get(normalized) || value.trim();
      };

      // Current year
      if (data.currentYearTrialBalance?.aiClassifiedData?.length > 0) {
        const currentYearItems = data.currentYearTrialBalance.aiClassifiedData.map((item, index) => ({
          id: index + 1,
          account: item.AC || "",
          description: item.Description || "",
          balance: item.Balance || "0",
          category: normalizeValue(item.Category, categoryMap),
          group: normalizeValue(item.Group, groupMap),
          subgroup: normalizeValue(item.Subgroup, subgroupMap),
          confidence: item.Confidence || 0,
          mapping: item.Mapping || "",
        }));
        setCurrentYearData(currentYearItems);
        setCurrentYearLowConfidenceCount(data.currentYearTrialBalance.lowConfidenceCount || 0);
      }

      // Prior year
      if (data.priorYearTrialBalance?.aiClassifiedData?.length > 0) {
        const priorYearItems = data.priorYearTrialBalance.aiClassifiedData.map((item, index) => ({
          id: index + 1,
          account: item.AC || "",
          description: item.Description || "",
          balance: item.Balance || "0",
          category: normalizeValue(item.Category, categoryMap),
          group: normalizeValue(item.Group, groupMap),
          subgroup: normalizeValue(item.Subgroup, subgroupMap),
          confidence: item.Confidence || 0,
          mapping: item.Mapping || "",
        }));
        setPriorYearData(priorYearItems);
        setPriorYearLowConfidenceCount(data.priorYearTrialBalance.lowConfidenceCount || 0);
      }

      // Set dropdown options
      setCategoryOptions(Array.from(categoryMap.values()).filter(Boolean).sort());
      setGroupOptions(Array.from(groupMap.values()).filter(Boolean).sort());
      setSubgroupOptions(Array.from(subgroupMap.values()).filter(Boolean).sort());
    };

    const fetchStatusAndProcess = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        const res = await axios.post(
          `${apiConfig.apiBaseURL}/getEngagementStatus/${engagementId}`,
          { module: "mapping" },
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const { status, data } = res.data;
        setStatus(status);

        if (status === "completed") {
          processMappingData(data);
          setLoading(false);
          setIsPolling(false);
          setStatus("");
          clearInterval(pollingInterval);
        } else if (status === "pending") {
          setStatus("pending");
          setIsPolling(true);
          setLoading(true);

          // Start polling
          pollingInterval = setInterval(async () => {
            try {
              const pollRes = await axios.post(
                `${apiConfig.apiBaseURL}/getEngagementStatus/${engagementId}`,
                { module: "mapping" },
                {
                  headers: {
                    "x-api-key": apiConfig["x-api-key"],
                    Authorization: `Bearer ${authToken}`,
                    "Content-Type": "application/json",
                  },
                }
              );

              const { status: newStatus, data: newData } = pollRes.data;

              if (newStatus === "completed") {
                clearInterval(pollingInterval);
                processMappingData(newData);
                setEngagement(newData);
                setLoading(false);
                setIsPolling(false);
                setStatus("");
              } else if (newStatus === "failed") {
                clearInterval(pollingInterval);
                setIsPolling(false);
                setLoading(false);
                setStatus("");
                toast.error("AI classification failed. Please try again");
              }
            } catch (pollError) {
              console.error("[Polling] Error:", pollError);
              clearInterval(pollingInterval);
              setLoading(false);
              setIsPolling(false);
            }
          }, 5000);
        }
      } catch (error) {
        console.error("Initial fetch error:", error);
        setLoading(false);
        setIsPolling(false);
      }
    };

    setIsLoading(true);
    fetchStatusAndProcess().finally(() => setIsLoading(false));

    return () => {
      clearInterval(pollingInterval);
      setIsPolling(false);
    };
  }, [engagementId]);




  // Extract engagement ID from URL
  useEffect(() => {
    // Extract engagementId from the URL
    const pathname = window.location.pathname;
    const match = pathname.match(/\/engagements\/edit-engagement-setup\/([^\/]+)/);
    if (match && match[1]) {
      setEngagementId(match[1]);
      // console.log("Engagement ID extracted from URL:", match[1]);
    } else if (mappingData && mappingData.data && mappingData.data.engagementId) {
      setEngagementId(mappingData.data.engagementId);
    } else {
      // console.error("Could not find engagement ID");
    }
  }, [mappingData]);

  // Process the data from the /engagementMapping API response
  useEffect(() => {
    const processApiData = () => {
      setIsLoading(true);
      const currentYearItems = [];
      const priorYearItems = [];
      let currentYearLowConfidenceCount = 0;
      let priorYearLowConfidenceCount = 0;

      if (mappingData) {
        // Create normalized lookup maps first
        const categoryMap = new Map();
        const groupMap = new Map();
        const subgroupMap = new Map();

        // Build lookup maps from all data
        [...(mappingData.currentYearTrialBalance?.aiClassifiedData || []), ...(mappingData.priorYearTrialBalance?.aiClassifiedData || [])].forEach(item => {
          if (item.Category && item.Category.trim()) {
            const trimmed = item.Category.trim();
            const normalized = trimmed.toLowerCase();
            if (!categoryMap.has(normalized)) {
              categoryMap.set(normalized, trimmed);
            }
          }
          if (item.Group && item.Group.trim()) {
            const trimmed = item.Group.trim();
            const normalized = trimmed.toLowerCase();
            if (!groupMap.has(normalized)) {
              groupMap.set(normalized, trimmed);
            }
          }
          if (item.Subgroup && item.Subgroup.trim()) {
            const trimmed = item.Subgroup.trim();
            const normalized = trimmed.toLowerCase();
            if (!subgroupMap.has(normalized)) {
              subgroupMap.set(normalized, trimmed);
            }
          }
        });

        // Helper function to normalize values
        const normalizeValue = (value, map) => {
          if (!value || !value.trim()) return "";
          const normalized = value.trim().toLowerCase();
          return map.get(normalized) || value.trim();
        };

        // Process current year data
        if (mappingData.currentYearTrialBalance &&
          mappingData.currentYearTrialBalance.aiClassifiedData) {

          // Get low confidence count
          currentYearLowConfidenceCount =
            mappingData.currentYearTrialBalance.lowConfidenceCount || 0;

          // Map the classified data to our UI format
          mappingData.currentYearTrialBalance.aiClassifiedData.forEach((item, index) => {
            currentYearItems.push({
              id: index + 1,
              account: item.AC || "",
              description: item.Description || "",
              balance: item.Balance || "0",
              category: normalizeValue(item.Category, categoryMap),
              group: normalizeValue(item.Group, groupMap),
              subgroup: normalizeValue(item.Subgroup, subgroupMap),
              confidence: item.Confidence || 0,
              mapping: item.Mapping || "",
            });
          });
        }

        // Process prior year data
        if (mappingData.priorYearTrialBalance &&
          mappingData.priorYearTrialBalance.aiClassifiedData) {

          // Get low confidence count
          priorYearLowConfidenceCount =
            mappingData.priorYearTrialBalance.lowConfidenceCount || 0;

          // Map the classified data to our UI format
          mappingData.priorYearTrialBalance.aiClassifiedData.forEach((item, index) => {
            priorYearItems.push({
              id: index + 1,
              account: item.AC || "",
              description: item.Description || "",
              balance: item.Balance || "0",
              category: normalizeValue(item.Category, categoryMap),
              group: normalizeValue(item.Group, groupMap),
              subgroup: normalizeValue(item.Subgroup, subgroupMap),
              confidence: item.Confidence || 0,
              mapping: item.Mapping || "",
            });
          });
        }
      }

      setCurrentYearData(currentYearItems);
      setPriorYearData(priorYearItems);
      setCurrentYearLowConfidenceCount(currentYearLowConfidenceCount);
      setPriorYearLowConfidenceCount(priorYearLowConfidenceCount);
      setIsLoading(false);
    };

    if (mappingData) {
      processApiData();
    }
  }, [mappingData]);

  useEffect(() => {
    const extractUniqueOptions = () => {
      if (mappingData) {
        const categories = new Map();
        const groups = new Map();
        const subgroups = new Map();

        // Process current year data
        if (mappingData.currentYearTrialBalance?.aiClassifiedData) {
          mappingData.currentYearTrialBalance.aiClassifiedData.forEach(
            (item) => {
              if (item.Category && item.Category.trim()) {
                const trimmed = item.Category.trim();
                const normalized = trimmed.toLowerCase();
                if (!categories.has(normalized)) {
                  categories.set(normalized, trimmed);
                }
              }
              if (item.Group && item.Group.trim()) {
                const trimmed = item.Group.trim();
                const normalized = trimmed.toLowerCase();
                if (!groups.has(normalized)) {
                  groups.set(normalized, trimmed);
                }
              }
              if (item.Subgroup && item.Subgroup.trim()) {
                const trimmed = item.Subgroup.trim();
                const normalized = trimmed.toLowerCase();
                if (!subgroups.has(normalized)) {
                  subgroups.set(normalized, trimmed);
                }
              }
            }
          );
        }

        // Process prior year data
        if (mappingData.priorYearTrialBalance?.aiClassifiedData) {
          mappingData.priorYearTrialBalance.aiClassifiedData.forEach(
            (item) => {
              if (item.Category && item.Category.trim()) {
                const trimmed = item.Category.trim();
                const normalized = trimmed.toLowerCase();
                if (!categories.has(normalized)) {
                  categories.set(normalized, trimmed);
                }
              }
              if (item.Group && item.Group.trim()) {
                const trimmed = item.Group.trim();
                const normalized = trimmed.toLowerCase();
                if (!groups.has(normalized)) {
                  groups.set(normalized, trimmed);
                }
              }
              if (item.Subgroup && item.Subgroup.trim()) {
                const trimmed = item.Subgroup.trim();
                const normalized = trimmed.toLowerCase();
                if (!subgroups.has(normalized)) {
                  subgroups.set(normalized, trimmed);
                }
              }
            }
          );
        }

        // Sort and set options
        setCategoryOptions(Array.from(categories.values()).filter(Boolean).sort());
        setGroupOptions(Array.from(groups.values()).filter(Boolean).sort());
        setSubgroupOptions(Array.from(subgroups.values()).filter(Boolean).sort());
      }
    };

    if (mappingData) {
      extractUniqueOptions();
    }
  }, [mappingData]);

  const itemsPerPage = 10;

  // Get current data based on active tab
  const data = activeTab === "currentYear" ? currentYearData : priorYearData;

  // Filter data based on search term
  const filteredData = data.filter((item) => {
    if (searchTerm === "") return true;

    const searchTermLower = searchTerm.toLowerCase();
    return (
      (item.account && item.account.toLowerCase().includes(searchTermLower)) ||
      (item.description &&
        item.description.toLowerCase().includes(searchTermLower)) ||
      (item.category &&
        item.category.toLowerCase().includes(searchTermLower)) ||
      (item.group && item.group.toLowerCase().includes(searchTermLower)) ||
      (item.subgroup && item.subgroup.toLowerCase().includes(searchTermLower))
    );
  });

  // Sorting function - only allow sorting for account and confidence
  const sortTable = (key) => {
    if (key !== "account" && key !== "confidence") return;

    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    if (sortConfig.key === "confidence") {
      return sortConfig.direction === "asc"
        ? Number(a.confidence) - Number(b.confidence)
        : Number(b.confidence) - Number(a.confidence);
    }

    if (sortConfig.key === "account") {
      if (a.account < b.account) return sortConfig.direction === "asc" ? -1 : 1;
      if (a.account > b.account) return sortConfig.direction === "asc" ? 1 : -1;
    }

    return 0;
  });

  // Get sort icon helper - only show sort icons for account and confidence
  const getSortIcon = (key) => {
    if (key !== "account" && key !== "confidence") {
      return null;
    }

    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? (
        <MdKeyboardArrowUp size={20} />
      ) : (
        <MdKeyboardArrowDown size={20} />
      );
    }
    return <RxCaretSort size={20} />;
  };

  // Pagination
  const offset = currentPage * itemsPerPage;
  const paginatedData = sortedData.slice(offset, offset + itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Handle changes to the classification data with cross-year synchronization
const handleDataChange = (itemId, field, value, isCurrentYear) => {
  if (isCurrentYear) {
    const updatedData = [...currentYearData];
    const indexToUpdate = updatedData.findIndex(item => item.id === itemId);
    if (indexToUpdate !== -1) {
      updatedData[indexToUpdate][field] = value;
      setCurrentYearData(updatedData);
      
      // Sync with prior year - find matching account
      const currentAccount = updatedData[indexToUpdate].account;
      if (currentAccount) {
        const priorYearIndex = priorYearData.findIndex(item => item.account === currentAccount);
        if (priorYearIndex !== -1) {
          const updatedPriorYearData = [...priorYearData];
          updatedPriorYearData[priorYearIndex][field] = value;
          setPriorYearData(updatedPriorYearData);
        }
      }
    }
  } else {
    const updatedData = [...priorYearData];
    const indexToUpdate = updatedData.findIndex(item => item.id === itemId);
    if (indexToUpdate !== -1) {
      updatedData[indexToUpdate][field] = value;
      setPriorYearData(updatedData);
      
      // Sync with current year - find matching account
      const priorAccount = updatedData[indexToUpdate].account;
      if (priorAccount) {
        const currentYearIndex = currentYearData.findIndex(item => item.account === priorAccount);
        if (currentYearIndex !== -1) {
          const updatedCurrentYearData = [...currentYearData];
          updatedCurrentYearData[currentYearIndex][field] = value;
          setCurrentYearData(updatedCurrentYearData);
        }
      }
    }
  }
};

  const prepareDataForSubmission = (shouldNavigate) => {
    // Make sure engagementId is defined and not null/undefined
    if (!engagementId) {
      toast.error("Engagement ID is missing");
      // console.error("Missing engagementId in Mapping component");
      return null;
    }

    // Format current year data
    const currentYearAiClassifiedData = currentYearData.map((item) => ({
      AC: item.account,
      Description: item.description,
      Balance: item.balance,
      Category: item.category,
      Group: item.group,
      Subgroup: item.subgroup,
      Confidence: item.confidence,
      Mapping: item.mapping,
    }));

    // Format prior year data
    const priorYearAiClassifiedData = priorYearData.map((item) => ({
      AC: item.account,
      Description: item.description,
      Balance: item.balance,
      Category: item.category,
      Group: item.group,
      Subgroup: item.subgroup,
      Confidence: item.confidence,
      Mapping: item.mapping,
    }));

    return {
      engagementId,
      currentYearAiClassifiedData,
      priorYearAiClassifiedData,
      mode: shouldNavigate == "exit" ? "save" : "par",
    };
  };

  // Handle saving the mapping changes
  const handleSaveMapping = async (shouldNavigate) => {
    try {
      setIsSaving(true);
      if (shouldNavigate === "exit") {
        setSaveExitLoading(true);
      } else if (shouldNavigate === "next") {
        setContinueParLoading(true);
        setIsGeneratingPar(true);
      }


      const authToken = localStorage.getItem("authToken");
      const payload = prepareDataForSubmission(shouldNavigate);

      // If payload is null, don't proceed with the API call
      if (!payload) {
        toast.error("Cannot save mapping: Missing engagement ID");
        setIsSaving(false);
        return;
      }

      // console.log("Sending payload:", payload);

      const response = await axios.post(
        `${apiConfig.apiBaseURL}/generatePAR`,
        payload,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        if (shouldNavigate === "exit") {
          toast.success("Mapping data saved successfully!");
        } else if (shouldNavigate === "next") {
          // toast.success("PAR generated successfully!");
        }
        setParData?.(response.data);

        if (shouldNavigate === "exit") {
          navigate('/engagements');
        } else if (shouldNavigate === "next") {
          onStepComplete();
          handleNext();
        }
      } else {
        throw new Error(response.data.message || "Failed to save mapping data");
      }
    } catch (error) {
      // console.error("Error saving mapping data:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to save mapping data. Please try again."
      );
    } finally {
      setIsSaving(false);
      setSaveExitLoading(false);
      setContinueParLoading(false);
      setIsGeneratingPar(false);
    }
  };

  // Format currency value
  const formatCurrency = (value) => {
    if (!value) return "$0.00";

    // Convert to number and check if it's a valid number
    const numValue = Number(value);
    if (isNaN(numValue)) return value;

    // Format as currency
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(numValue);
  };

  // Helper function to determine if header should be sortable
  const isHeaderSortable = (key) => {
    return key === "account" || key === "confidence";
  };



  if (status === "pending") {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          zIndex: 1050,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "420px",
            backgroundColor: "#fff",
            borderRadius: "16px",
            padding: "40px 20px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            textAlign: "center",
          }}
        >
          <img
            src={image}
            alt="Processing"
            style={{
              width: "120px",
              marginBottom: "20px",
            }}
          />
          <h5 style={{ fontWeight: 500, fontSize: "18px", color: "#333" }}>
            The AI is working on it.
          </h5>
          {/* <p style={{ fontSize: "14px", color: "#666" }}>
            This might take a little time, so please be patient.
          </p> */}

          <div className='d-flex justify-content-center'>
            <ReactTextTransition>
              {getMessage()}
            </ReactTextTransition>
          </div>
          {extraMessage && (
            <p style={{ fontSize: "14px", color: "#999", marginTop: "10px" }}>
              {extraMessage}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <><Loader /></>
  }

  return (
    <>
      {/* <ToastContainer position="top-right" autoClose={3000} /> */}
      <Container fluid className="custom-tab mapping engagement-dashboard">
        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between border-bottom">
              <Tabs
                activeKey={activeTab}
                onSelect={(tab) => {
                  setActiveTab(tab);
                  setCurrentPage(0); // Reset to first page when switching tabs
                }}
                className="border-0 w-75"
              >
                <Tab
                  eventKey="currentYear"
                  title={`Current Year TB (${currentYearData.length})`}
                />
                <Tab
                  eventKey="priorYear"
                  title={`Prior Year TB (${priorYearData.length})`}
                />
              </Tabs>
              <InputGroup className="w-50 credential-page mb-2 b">
                <InputGroup.Text
                  id="search"
                  className="border-0 px-1"
                  style={{ backgroundColor: "#E7EFF8" }}
                >
                  <CiSearch size={22} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Account#, Description, Category, Group, Sub Group"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-describedby="search"
                />
              </InputGroup>
            </div>
          </Col>
        </Row>

        {(activeTab === "currentYear"
          ? currentYearLowConfidenceCount
          : priorYearLowConfidenceCount) > 0 && (
            <div className="mb-2" style={{ backgroundColor: "#FFEDED" }}>
              <p className="mb-0 text-danger text-center p-2">
                <IoWarningOutline className="text-danger me-1" />
                There are{" "}
                {activeTab === "currentYear"
                  ? currentYearLowConfidenceCount
                  : priorYearLowConfidenceCount}{" "}
                line items that AI is not confident about.
              </p>
            </div>
          )}

        {data.length === 0 ? (
          <div className="text-center my-5">
            <img src={ImageNoDataFound} alt="No Data Found" />
            <p className="fw-bold text-dark mb-0">
              No mapping data available for{" "}
              {activeTab === "currentYear" ? "Current Year" : "Prior Year"}
            </p>
          </div>
        ) : (
          <>
            <Table hover responsive style={{ border: "2px solid #E7ECF1" }}>
              <thead>
                <tr style={{ whiteSpace: "nowrap" }}>
                  <th
                    className={`text-light p-3 ${isHeaderSortable("account") ? "cursor-pointer" : ""
                      }`}
                    onClick={() => sortTable("account")}
                  >
                    Ac# {getSortIcon("account")}
                  </th>
                  <th className="text-light p-3">Description</th>
                  <th className="text-light p-3">Balance</th>
                  <th className="text-light p-3">Category</th>
                  <th className="text-light p-3">Group</th>
                  <th className="text-light p-3">Sub Group</th>
                  <th
                    className={`text-light p-3 ${isHeaderSortable("confidence") ? "cursor-pointer" : ""
                      }`}
                    onClick={() => sortTable("confidence")}
                  >
                    Confidence {getSortIcon("confidence")}
                  </th>
                </tr>
              </thead>
              <tbody className="credential-page">
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => {
                    // Calculate the actual index in the full data array
                    const actualIndex = currentPage * itemsPerPage + index;
                    const isCurrentYear = activeTab === "currentYear";

                    return (
                      <tr key={item.id} style={{ whiteSpace: "nowrap" }}>
                        <td className="text-light align-middle">{item.account}</td>
                        <td className="text-light align-middle">{item.description}</td>
                        <td className="text-light align-middle">
                          {formatCurrency(item.balance)}
                        </td>
                        <td>
                          <Form.Select
                            className="py-2 text-light"
                            value={item.category}
                            onChange={(e) =>
                              handleDataChange(
                                item.id,  // Pass item.id instead of actualIndex
                                "category",
                                e.target.value,
                                isCurrentYear
                              )
                            }
                          >
                            <option value="">Select Category</option>
                            {categoryOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            className="align-middle text-light"
                            style={{ width: '180px' }}
                            value={item.group}
                            onChange={(e) =>
                              handleDataChange(
                                item.id,  // Pass item.id instead of actualIndex
                                "group",
                                e.target.value,
                                isCurrentYear
                              )
                            }
                          >
                            <option value="">Select Group</option>
                            {groupOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                        <Form.Select
                          className="align-middle text-light"
                          value={item.subgroup}
                          onChange={(e) =>
                            handleDataChange(
                              item.id,  // ✅ CORRECT - use item.id
                              "subgroup",
                              e.target.value,
                              isCurrentYear
                            )
                          }
                        >
                            <option value="">Select Subgroup</option>
                            {subgroupOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td
                          style={{ width: "120px" }}
                          className={`align-middle ${Number(item.confidence) >= 75 ? "green" : "red"
                            }`}
                        >
                          <span>{item.confidence}%</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      <div>
                        <img src={ImageNoDataFound} alt="No Data Found" />
                        <p className="fw-bold text-dark mb-0">No data found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <div className="d-flex justify-content-between align-items-center">
              <p>
                Showing: {offset + 1}-
                {Math.min(offset + itemsPerPage, filteredData.length)} of{" "}
                {filteredData.length}
              </p>
              <ReactPaginate
                previousLabel={"<"}
                nextLabel={">"}
                breakLabel="..."
                pageCount={Math.ceil(filteredData.length / itemsPerPage)}
                marginPagesDisplayed={2}
                pageRangeDisplayed={2}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                activeClassName={"active"}
                forcePage={currentPage}
              />
            </div>
          </>
        )}
      </Container>
      <Container fluid className="px-0 setup-footer">
        <Card
          className="h-100 py-4 px-3 mt-3"
          style={{ border: "2px solid #ECEEF5" }}
        >
          <div className="d-flex justify-content-between mt-3 all-buttons">
            <div className="left">
              <Button
                variant="outline-primary"
                className="px-5 py-3 me-3"
                onClick={() => handleSaveMapping("exit")}
                disabled={isSaving}
              >
                {saveExitLoading ? "Saving..." : "Save & Exit"}
              </Button>
              <Button
                variant="outline-primary"
                className="px-5 py-3 me-3"
                onClick={handlePrevious}
                disabled={isSaving}
              >
                Back to Setup
              </Button>
            </div>
            <div className="right">
              {/* <Button
                variant="outline-primary"
                className="px-5 py-3 me-3"
                onClick={goToDocumentSummary}
              >
                Skip to Document Summary
              </Button> */}
              <Button
                variant="primary"
                className="px-5 py-3"
                onClick={() => handleSaveMapping("next")}
                disabled={data.length === 0 || isSaving}
              >
                {continueParLoading ? "Loading..." : "Continue to PAR"}
              </Button>
            </div>
          </div>
        </Card>
      </Container>
    </>
  );
};

export default Mapping;